<script lang="ts">
  import DatasetMatchKeys from '$shared/atoms/DatasetMatchKeys/DatasetMatchKeys.svelte';
  import Card from '$shared/atoms/Card/Card.svelte';
  import Title from '$shared/atoms/Title/Title.svelte';
  import Tab from '$shared/atoms/Tab/Tab.svelte';
  import Tag from '$shared/atoms/Tag/Tag.svelte';
  import { TagSize } from '$shared/constants/TagSize';
  import Loader from '$shared/atoms/Loader/Loader.svelte';
  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import Spinner from '$shared/atoms/Spinner/Spinner.svelte';
  import Menu from '$shared/molecules/Menu/Menu.svelte';

  import List from './_partials/List.svelte';
  import ActionsComp from './_partials/Actions.svelte';
  import { setContext, tick, type Snippet, untrack } from 'svelte';
  import type { IMonitoringConfig } from '$provider/lib/apis/interfaces/IResponse';
  import EntityMeta from '$shared/atoms/EntityMeta/EntityMeta.svelte';
  import AlertInfo from '../_EntityView/partials/AlertInfo.svelte';
  import { afterNavigate } from '$app/navigation';
  import { TagTypes } from '$shared/atoms/Tag/TagProp';
  import EntityProcessLoader from '$shared/molecules/EntityProcessLoader/EntityProcessLoader.svelte';
  import { EntityAction } from '$shared/constants/EntityAction';
  import { EntityTab } from '$shared/constants/EntityTab';

  setContext('', {
    Actions: EntityAction
  });

  interface Props {
    readonly?: boolean;
    loading?: boolean;
    processing?: boolean;
    hideActions?: boolean;
    isDataset?: boolean;
    showResultMatchKey?: boolean;
    title?: string;
    results?: any;
    alert?: any;
    providedInfos?: any;
    showNotes?: boolean;
    actions?: Actions[];
    options?: CommonList[];
    optionLoading?: boolean;
    hideProvidedInfo?: boolean;
    hasCustomResult?: boolean;
    metaInfo?: any;
    monitoringConfig?: IMonitoringConfig;
    tag?: string;
    isNeedsReview?: boolean;
    name?: string;
    alertMessage?: string;
    hasVerification?: boolean;
    verificationProcessingLabel?: string | undefined;
    customActions?: Snippet | undefined;
    header?: Snippet | undefined;
    meta?: Snippet | undefined;
    result?: Snippet | undefined;
    screenshot?: Snippet | undefined;
    resultMeta?: Snippet | undefined;
    ontabchanged?: (tab: any) => void;
    onclickoption?: (option: any) => void;
    onclickalert?: (alert: any) => void;
    onedit?: () => void;
    onremove?: () => void;
    onreverify?: () => void;
    onevent?: (payload: any) => void;
  }

  let {
    readonly = false,
    loading = false,
    processing = false,
    hideActions = false,
    isDataset = false,
    showResultMatchKey = false,
    title = '',
    results = [],
    alert = null,
    providedInfos = [],
    showNotes = false,
    actions = [],
    options = [],
    optionLoading = false,
    hideProvidedInfo = false,
    hasCustomResult = false,
    metaInfo = {},
    monitoringConfig = null,
    tag = '',
    isNeedsReview = false,
    name = '',
    alertMessage = '',
    hasVerification = false,
    verificationProcessingLabel = undefined,
    customActions = undefined,
    header = undefined,
    meta = undefined,
    result = undefined,
    screenshot = undefined,
    resultMeta = undefined,
    ontabchanged,
    onclickoption,
    onclickalert,
    onedit,
    onremove,
    onreverify,
    onevent
  }: Props = $props();
  export const refreshTab = async (): Promise<void> => {
    await setActiveTabAsync();
  };

  let DefaultTabs = $derived({
    result: {
      id: 'result',
      label: 'Verified Results',
      hide: results.length === 0 && !hasCustomResult
    },
    providedInfo: { id: 'providedInfo', label: 'Provided Info', hide: hideProvidedInfo },
    note: { id: 'note', label: 'Add Note', icon: 'icon-plus', hide: !showNotes }
  });
  let tabs = $derived(Object.values(DefaultTabs).filter((tab) => !tab.hide));
  let activeTab = $state(tabs?.first()?.id ?? '');
  $effect(() => {
    const tabList = tabs;
    const currentActiveTab = untrack(() => activeTab);
    const tempActiveTab = currentActiveTab || tabList?.first()?.id;
    if (!tabList.find((tab) => tab.id === tempActiveTab)) {
      activeTab = tabList?.first()?.id ?? '';
    } else {
      activeTab = tempActiveTab ?? '';
    }
  });

  afterNavigate(() => {
    onRouteChange();
  });

  const onRouteChange = async (): Promise<void> => {
    await setActiveTabAsync();
  };

  const setActiveTabAsync = async (): Promise<void> => {
    await tick();
    activeTab = tabs?.first()?.id ?? '';
  };

  const onClickTab = (tab) => {
    activeTab = tab.id;
    ontabchanged?.(tab);
  };

  const onClickOptions = (option) => {
    onclickoption?.(option);
  };

  const onClickAlert = () => {
    onclickalert?.(alert);
  };
</script>

<div data-cy={`${name}-entity`} class="entity-view">
  {#if alert && alert.label}
    <button data-cy="alert-label" class="warn-banner" onclick={onClickAlert}>
      <div class="text">{alert.label}</div>
      {#if alert.action}
        <div class="separator">-</div>
        <div class="action">{alert.action}</div>
      {/if}
      <div class="icon"><Icon value="icon-chevron-right" /></div>
    </button>
  {/if}
  <Card>
    <div class="header">
      <div data-cy={`${name}-header`} class="title">
        <Title type="h2">{title || ''}</Title>
        {#if tag}
          <div data-cy={`${name}-status`} class="tag">
            <Tag
              name={tag}
              type={isNeedsReview ? TagTypes.Warn : TagTypes.None}
              title={tag}
              size={TagSize.Small}>{tag}</Tag
            >
          </div>
        {/if}
      </div>
      <div class="actions-wrap">
        {#if !hideActions}
          {#if !loading && !processing}
            {@render customActions?.()}
          {/if}
          <ActionsComp
            loading={loading || processing}
            list={actions}
            {readonly}
            {onedit}
            {onremove}
            {onreverify}
          />
          {#if !loading && !processing && options && options.length !== 0}
            <div class="options">
              {#if optionLoading}
                <Spinner />
              {:else}
                <Menu list={options} onclick={onClickOptions} />
              {/if}
            </div>
          {/if}
        {/if}
      </div>
    </div>
    {@render header?.()}
    <div class="tab">
      <Tab list={tabs} active={activeTab} onclick={onClickTab} />
    </div>
    <div class="tab-container">
      <AlertInfo message={alertMessage} />
      <div class="meta">
        <EntityMeta meta={metaInfo} showIcon monitorConfig={monitoringConfig} />
      </div>
      {@render meta?.()}
      {#if activeTab === DefaultTabs.result.id}
        {@render resultMeta?.()}
        <div data-cy={`${name}-result`} class="result">
          {#if loading && hasVerification}
            <EntityProcessLoader label={verificationProcessingLabel} />
          {:else if loading}
            <div class="loader">
              <Loader count={3} />
            </div>
          {:else if hasCustomResult}
            {@render result?.()}
          {:else}
            <List dataList={results} {onevent} />
            {@render screenshot?.()}
          {/if}
        </div>
      {:else if activeTab === DefaultTabs.providedInfo.id}
        <List name={`${name}-result`} dataList={providedInfos} />
      {:else if activeTab === DefaultTabs.note.id}
        <div class="notes">Notes</div>
      {:else}
        <div class="empty">Invalid tab chosen</div>
      {/if}
    </div>
  </Card>
  {#if isDataset && (showResultMatchKey || activeTab === DefaultTabs.providedInfo.id)}
    <div class="dataset-match-key">
      <DatasetMatchKeys />
    </div>
  {/if}
</div>

<style>
  .warn-banner {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--yellow);
    padding: 8px 16px;
    border-radius: 2px 2px 0px 0px;
    position: relative;
  }
  .warn-banner:hover {
    background-color: var(--yellow-hover);
  }
  .warn-banner .separator {
    margin: 0 4px;
  }
  .warn-banner .action {
    text-decoration: underline;
  }

  .icon {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translate(0, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }
  .title {
    display: flex;
    align-items: center;
  }
  .title .tag {
    margin-left: 8px;
  }
  .tab {
    margin-top: 32px;
    margin-bottom: 24px;
  }
  .empty {
    background-color: var(--grey-bg);
    line-height: 30px;
    padding: 0 8px;
    color: var(--grey-medium);
    font-style: italic;
  }
  .actions-wrap {
    display: flex;
    align-items: center;
  }
  .options {
    margin-left: 8px;
  }
  .result {
    width: 100%;
    display: flex;
    overflow: hidden;
  }
  .result .loader {
    width: 100%;
  }
  .meta {
    margin-bottom: 24px;
    display: flex;
    align-items: center;
  }
  .dataset-match-key {
    margin-top: 15px;
  }
</style>
